import { useState } from 'react';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import MaskEdit from '../../../organisms/MaskEdit';
import CurrencyInput from '../../../molecules/CurrencyInput';
import { VEHICLE_TYPE_OPTIONS, COST_TYPE_OPTIONS } from '../../../../types/Vehicle';
import type { VehicleCost, VehicleType as VehicleTypeValue } from '../../../../types/Vehicle';
import type { ErrorField } from '../../../../types/ErrorField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';

const UF_OPTIONS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT','PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO']
    .map(uf => ({ value: uf, label: uf }));

interface VehicleFormProps {
    isCreating: boolean;
    errorFields: ErrorField[];
    name: string; setName: (v: string) => void;
    vehicleType: VehicleTypeValue; setVehicleType: (v: VehicleTypeValue) => void;
    brand: string; setBrand: (v: string) => void;
    model: string; setModel: (v: string) => void;
    chassis: string; setChassis: (v: string) => void;
    renavam: string; setRenavam: (v: string) => void;
    plate: string; setPlate: (v: string) => void;
    yearManufacture: number; setYearManufacture: (v: number) => void;
    yearModel: number; setYearModel: (v: number) => void;
    color: string; setColor: (v: string) => void;
    plateState: string; setPlateState: (v: string) => void;
    comments: string; setComments: (v: string) => void;
    creditOperationId: string; setCreditOperationId: (v: string) => void;
    obligations: any[]; isLoadingObligations: boolean;
    owner: { personId: string; name: string; document: string };
    setOwner: (v: { personId: string; name: string; document: string }) => void;
    ownerSearch: string; setOwnerSearch: (v: string) => void;
    ownerResults: any[]; isSearchingOwner: boolean; showOwnerDropdown: boolean;
    searchOwner: (query: string) => void; handleSelectOwner: (person: any) => void;
    valuation: any; setValuation: (v: any) => void;
    costs: VehicleCost[]; addCost: (c: VehicleCost) => void; removeCost: (i: number) => void;
}

const VehicleForm = ({
    isCreating, errorFields,
    name, setName,
    vehicleType, setVehicleType,
    brand, setBrand,
    model, setModel,
    chassis, setChassis,
    renavam, setRenavam,
    plate, setPlate,
    yearManufacture, setYearManufacture,
    yearModel, setYearModel,
    color, setColor,
    plateState, setPlateState,
    comments, setComments,
    creditOperationId, setCreditOperationId,
    obligations, isLoadingObligations,
    owner, setOwner,
    ownerSearch, setOwnerSearch,
    ownerResults, isSearchingOwner, showOwnerDropdown,
    searchOwner, handleSelectOwner,
    valuation, setValuation,
    costs, addCost, removeCost,
}: VehicleFormProps) => {
    const [showCostForm, setShowCostForm] = useState(false);
    const [costType, setCostType] = useState('');
    const [costValue, setCostValue] = useState(0);
    const [costDate, setCostDate] = useState('');
    const [costRegNumber, setCostRegNumber] = useState('');
    const [costRegDate, setCostRegDate] = useState('');

    const handleAddCost = () => {
        if (!costType || !costValue || !costDate) return;
        addCost({
            costType: costType as any,
            value: costValue,
            costDate,
            registrationNumber: costRegNumber || undefined,
            registrationDate: costRegDate || undefined,
        });
        setCostType(''); setCostValue(0); setCostDate('');
        setCostRegNumber(''); setCostRegDate('');
        setShowCostForm(false);
    };

    return (
        <div className="p-4 space-y-6">
            {/* Obrigação */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Obrigação *</h4>
                <SelectTextField label="Selecione a obrigação *" value={creditOperationId}
                    onChange={e => setCreditOperationId(e.target.value)}
                    items={obligations.map((o: any) => ({
                        value: o.creditOperationId,
                        label: `${o.operationNumber} — ${o.assignorName || o.operationCode || ''}`,
                    }))}
                    emptyLabel={isLoadingObligations ? 'Carregando...' : 'Selecione'}
                    errorMessage={getErrorMessage(errorFields, 'creditOperationId')} />
            </section>

            {/* Dados do Veículo */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Dados do Veículo</h4>
                <div className="grid grid-cols-3 gap-3">
                    <SelectTextField label="Tipo *" value={vehicleType}
                        onChange={(e) => setVehicleType(e.target.value as VehicleTypeValue)}
                        items={VEHICLE_TYPE_OPTIONS}
                        errorMessage={getErrorMessage(errorFields, 'vehicleType')} />
                    <TextEdit label="Marca *" value={brand} onChange={e => setBrand(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'brand')} />
                    <TextEdit label="Modelo *" value={model} onChange={e => setModel(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'model')} />
                </div>

                <div className="grid grid-cols-4 gap-3 mt-3">
                    <TextEdit label="Placa *" value={plate} maxLength={7}
                        onChange={e => setPlate(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        errorMessage={getErrorMessage(errorFields, 'plate')} />
                    <SelectTextField label="UF Placa *" value={plateState}
                        onChange={e => setPlateState(e.target.value)}
                        items={UF_OPTIONS} emptyLabel="Selecione"
                        errorMessage={getErrorMessage(errorFields, 'plateState')} />
                    <TextEdit label="Ano Fabricação *" type="number" value={String(yearManufacture)}
                        onChange={e => setYearManufacture(e.target.value ? +e.target.value : 0)}
                        errorMessage={getErrorMessage(errorFields, 'yearManufacture')} />
                    <TextEdit label="Ano Modelo *" type="number" value={String(yearModel)}
                        onChange={e => setYearModel(e.target.value ? +e.target.value : 0)}
                        errorMessage={getErrorMessage(errorFields, 'yearModel')} />
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3">
                    <TextEdit label="Chassi *" value={chassis} maxLength={17}
                        onChange={e => setChassis(e.target.value.toUpperCase())}
                        errorMessage={getErrorMessage(errorFields, 'chassis')} />
                    <MaskEdit label="RENAVAM *" value={renavam}
                        mask="00000000000" replacement={{ '0': /[0-9]/ }}
                        onChange={e => setRenavam(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'renavam')} />
                    <TextEdit label="Cor" value={color} onChange={e => setColor(e.target.value)} />
                </div>
            </section>

            {/* Proprietário */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Proprietário</h4>
                <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                        <TextEdit label="Nome / Razão Social (Enter para buscar) *" value={ownerSearch}
                            onChange={e => setOwnerSearch(e.target.value)}
                            onKeyDown={(e: any) => e.key === 'Enter' && searchOwner(ownerSearch)}
                            errorMessage={getErrorMessage(errorFields, 'ownerName')} />
                        {isSearchingOwner && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                        {showOwnerDropdown && ownerResults.length > 0 && (
                            <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                                {ownerResults.slice(0, 8).map((p: any, idx: number) => (
                                    <div key={p.personId || idx} onClick={() => handleSelectOwner(p)}
                                        className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                        <span className="font-medium dark:text-white">{p.name}</span>
                                        {p.documentFormatted && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{p.documentFormatted}</span>}
                                        <span className="text-[10px] text-gray-400 ml-1">({p.type})</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <TextEdit label="CPF/CNPJ" value={owner.document} disabled
                        errorMessage={getErrorMessage(errorFields, 'ownerDocument')} />
                </div>
                {owner.personId && (
                    <div className="mt-2 text-xs text-green-600 dark:text-green-400">
                        ✓ Proprietário selecionado: {owner.name} ({owner.document})
                    </div>
                )}
                {getErrorMessage(errorFields, 'personId') && !owner.personId && (
                    <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                        {getErrorMessage(errorFields, 'personId')}
                    </div>
                )}
            </section>

            {/* Avaliação */}
            <section>
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Avaliação (opcional)</h4>
                <div className="grid grid-cols-3 gap-3">
                    <CurrencyInput label="Valor de Mercado" prefix="R$" fractionDigits={2}
                        value={valuation?.marketValue || 0}
                        onChange={(v) => setValuation({ ...(valuation || { marketValue: 0, eligibleValue: 0, monthlyRate: 0, sourceName: 'FIPE', valuationDate: '' }), marketValue: v })} />
                    <TextEdit label="Tabela/Agência" value={valuation?.sourceName || ''}
                        onChange={e => setValuation({ ...(valuation || { marketValue: 0, eligibleValue: 0, monthlyRate: 0, sourceName: '', valuationDate: '' }), sourceName: e.target.value })} />
                    <TextEdit label="Data Avaliação" type="date" value={valuation?.valuationDate || ''}
                        onChange={e => setValuation({ ...(valuation || { marketValue: 0, eligibleValue: 0, monthlyRate: 0, sourceName: 'FIPE', valuationDate: '' }), valuationDate: e.target.value })} />
                </div>
            </section>

            {/* Custos / Registros */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Custos / Registros (opcional)</h4>
                    {!showCostForm && (
                        <button onClick={() => setShowCostForm(true)}
                            className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Adicionar</button>
                    )}
                </div>

                {costs.length > 0 && (
                    <div className="space-y-1 mb-3">
                        {costs.map((c, i) => (
                            <div key={i} className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-slate-800 rounded text-xs border border-gray-100 dark:border-slate-700">
                                <div className="flex items-center gap-3">
                                    <span className="font-medium text-gray-700 dark:text-gray-300">{COST_TYPE_OPTIONS.find(o => o.value === c.costType)?.label || c.costType}</span>
                                    <span className="text-gray-500">R$ {c.value.toFixed(2)}</span>
                                    <span className="text-gray-400">{c.costDate}</span>
                                    {c.registrationNumber && <span className="text-gray-400">Nº {c.registrationNumber}</span>}
                                </div>
                                <button onClick={() => removeCost(i)} className="text-red-500 hover:text-red-700 text-xs font-medium">Remover</button>
                            </div>
                        ))}
                    </div>
                )}

                {showCostForm && (
                    <div className="p-3 border border-gray-200 dark:border-slate-700 rounded-lg space-y-3 bg-gray-50 dark:bg-slate-800/50">
                        <div className="grid grid-cols-3 gap-3">
                            <SelectTextField label="Tipo *" value={costType}
                                onChange={e => setCostType(e.target.value)} items={COST_TYPE_OPTIONS} emptyLabel="Selecione" />
                            <CurrencyInput label="Valor *" prefix="R$" fractionDigits={2} value={costValue}
                                onChange={v => setCostValue(v)} />
                            <TextEdit label="Data *" type="date" value={costDate}
                                onChange={e => setCostDate(e.target.value)} />
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                            <TextEdit label="Nº Registro" value={costRegNumber}
                                onChange={e => setCostRegNumber(e.target.value)} />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button onClick={() => setShowCostForm(false)}
                                className="px-3 py-1.5 text-sm text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300">Cancelar</button>
                            <button onClick={handleAddCost}
                                className="px-3 py-1.5 text-sm text-white bg-gray-600 rounded-lg hover:bg-gray-700">Adicionar</button>
                        </div>
                    </div>
                )}
            </section>

            {/* Comentários */}
            <section>
                <TextEdit label="Comentários" value={comments}
                    onChange={e => setComments(e.target.value)} />
            </section>
        </div>
    );
};

export default VehicleForm;
